import React, { useState, useMemo } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2,
  CheckCircle2, XCircle, Clock, Users, Shield, Loader2
} from "lucide-react";

const C = {
  bg: "#060C18", card: "#0E1C35", cardHigh: "#122040",
  border: "rgba(255,255,255,0.07)",
  cyan: "#22D3EE", cyanSoft: "rgba(34,211,238,0.08)", cyanBright: "#67E8F9",
  blue: "#3B82F6", blueSoft: "rgba(59,130,246,0.1)",
  emerald: "#10B981", emeraldSoft: "rgba(16,185,129,0.1)",
  amber: "#FBBF24", amberSoft: "rgba(251,191,36,0.1)",
  rose: "#FB7185", roseSoft: "rgba(251,113,133,0.1)",
  t1: "#EFF6FF", t2: "#93B4D4", t3: "#4D6E90", t4: "#2A4060",
};

const STAFF_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
.sf-root,.sf-root *{font-family:'Outfit',system-ui,sans-serif!important;box-sizing:border-box}
.sf-root .sf-row{transition:background .15s ease}
.sf-root .sf-row:hover{background:rgba(34,211,238,0.04)!important}

[data-radix-dropdown-menu-content]{background:#0E1C35!important;border:1px solid rgba(34,211,238,0.18)!important;border-radius:12px!important;box-shadow:0 20px 50px rgba(0,0,0,0.8)!important;padding:4px!important;font-family:'Outfit',system-ui,sans-serif!important}
[data-radix-dropdown-menu-content] *{font-family:'Outfit',system-ui,sans-serif!important}
[data-radix-dropdown-menu-item]{color:#93B4D4!important;border-radius:7px!important;font-size:13px!important;font-weight:500!important;padding:8px 12px!important;cursor:pointer!important;transition:background .13s,color .13s!important;outline:none!important}
[data-radix-dropdown-menu-item]:hover,[data-radix-dropdown-menu-item][data-highlighted]{background:rgba(34,211,238,0.11)!important;color:#22D3EE!important;outline:none!important}
[data-radix-dropdown-menu-separator]{background:rgba(255,255,255,0.07)!important;margin:3px 0!important}

::-webkit-scrollbar{width:4px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:rgba(34,211,238,0.18);border-radius:99px}
`;

function useInjectCSS(id, css) {
  React.useEffect(() => {
    if (document.getElementById(id)) return;
    const el = document.createElement("style"); el.id = id; el.textContent = css;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);
}

const statusCfg = {
  ACTIVE: { icon: CheckCircle2, color: "#10B981", bg: "rgba(16,185,129,0.1)", label: "Active" },
  SUSPENDED: { icon: XCircle, color: "#FB7185", bg: "rgba(251,113,133,0.1)", label: "Suspended" },
  INACTIVE: { icon: Clock, color: "#FBBF24", bg: "rgba(251,191,36,0.1)", label: "Inactive" },
};

const Th = ({ children }) => (
  <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.t3, borderBottom: `1px solid ${C.border}`, background: C.cardHigh, whiteSpace: "nowrap" }}>
    {children}
  </th>
);
const Td = ({ children, style = {} }) => (
  <td style={{ padding: "14px 16px", fontSize: 13, color: C.t2, borderBottom: `1px solid ${C.border}`, ...style }}>
    {children}
  </td>
);

export default function AdminStaff() {
  const [search, setSearch] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  useInjectCSS("sf-css", STAFF_CSS);

  const { data: staffUsers = [], isLoading } = useQuery({ queryKey: ["admin-staff"], queryFn: () => adminService.getMyStaffUsers() });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateStaffUser(id, data),
    onSuccess: () => { queryClient.invalidateQueries(["admin-staff"]); setStatusDialog(null); },
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => adminService.deleteStaffUser(id),
    onSuccess: () => { queryClient.invalidateQueries(["admin-staff"]); setDeleteDialog(null); },
  });

  const filteredStaff = staffUsers.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleStatus = async (e) => {
    e.preventDefault();
    if (!statusDialog) return;
    await updateMutation.mutateAsync({ id: statusDialog.id, data: { name: statusDialog.name, status: statusDialog.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE" } });
  };

  return (
    <div className="sf-root" style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Outfit',system-ui,sans-serif" }}>
      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6">

        {/* Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row">
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: C.blueSoft, border: "1px solid rgba(59,130,246,0.22)" }}>
              <Users size={22} style={{ color: C.blue }} />
            </div>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 800, color: C.t1, margin: 0, letterSpacing: "-0.5px" }}>{t("staffMembers")}</h1>
              <p style={{ fontSize: 13, color: C.t3, margin: "3px 0 0", fontWeight: 500 }}>{staffUsers.length} {t("staffMembersCount")}</p>
            </div>
          </div>
          <Link to={createPageUrl("AdminStaffForm")}>
            <button style={{ width: "100%", display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", height: 40, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", background: C.cyan, color: C.bg, border: "none", transition: "all .18s ease", boxShadow: "0 0 20px rgba(34,211,238,0.25)" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.cyanBright; e.currentTarget.style.boxShadow = "0 0 28px rgba(34,211,238,0.38)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.cyan; e.currentTarget.style.boxShadow = "0 0 20px rgba(34,211,238,0.25)"; }}>
              <Plus size={16} />{t("createStaffMember")}
            </button>
          </Link>
        </div>

        {/* Search */}
        <div style={{ position: "relative", maxWidth: 380 }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.t3, pointerEvents: "none" }} />
          <input type="text" placeholder={t("searchStaff")} value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", height: 40, paddingLeft: 34, paddingRight: 12, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: C.t1, fontSize: 13, fontWeight: 500, outline: "none", transition: "border-color .18s,box-shadow .18s" }}
            onFocus={e => { e.target.style.borderColor = "rgba(34,211,238,0.4)"; e.target.style.boxShadow = "0 0 0 2px rgba(34,211,238,0.1)"; }}
            onBlur={e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = "none"; }} />
        </div>

        {/* Table */}
        <div style={{ borderRadius: 16, overflow: "auto", border: `1px solid ${C.border}`, background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)` }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>{t("staffMember")}</Th>
                <Th>{t("roles")}</Th>
                <Th>{t("status")}</Th>
                <Th>{t("created")}</Th>
                <Th style={{ textAlign: "right" }}>{t("actions")}</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={5} style={{ padding: "48px 0", textAlign: "center", color: C.t3, fontSize: 13 }}>{t("loading")}</td></tr>
              ) : filteredStaff.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: "48px 0", textAlign: "center", color: C.t3, fontSize: 13 }}>{t("noStaffMembersFound")}</td></tr>
              ) : filteredStaff.map(staff => {
                const st = statusCfg[staff.status] || statusCfg.INACTIVE;
                const StatusIcon = st.icon;
                return (
                  <tr key={staff.id} className="sf-row">
                    <Td>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 38, height: 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: C.blueSoft, border: "1px solid rgba(59,130,246,0.18)", flexShrink: 0 }}>
                          <Users size={16} style={{ color: C.blue }} />
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.t1 }}>{staff.name}</p>
                          <p style={{ margin: "2px 0 0", fontSize: 12, color: C.t3 }}>{staff.email}</p>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {staff.roles && staff.roles.length > 0 ? staff.roles.map(role => (
                          <span key={role.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: C.cyan, background: C.cyanSoft, border: "1px solid rgba(34,211,238,0.2)", padding: "3px 9px", borderRadius: 7 }}>
                            <Shield size={11} />{role.name}
                          </span>
                        )) : <span style={{ fontSize: 12, color: C.t4 }}>{t("noRolesAssigned")}</span>}
                      </div>
                    </Td>
                    <Td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: st.color, background: st.bg, border: `1px solid ${st.color}30`, padding: "4px 10px", borderRadius: 8 }}>
                        <StatusIcon size={12} />{st.label}
                      </span>
                    </Td>
                    <Td style={{ fontSize: 12, color: C.t3 }}>
                      {format(new Date(staff.createdAt), "MMM d, yyyy")}
                    </Td>
                    <Td style={{ textAlign: "right" }}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, background: "transparent", border: "1px solid transparent", cursor: "pointer", color: C.t3, transition: "all .15s", marginLeft: "auto" }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.cardHigh; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t1; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.color = C.t3; }}>
                            <MoreHorizontal size={15} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={createPageUrl("AdminStaffForm") + `?id=${staff.id}`} style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
                              <Pencil size={14} />{t("edit")}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setStatusDialog({ id: staff.id, name: staff.name, status: staff.status })}>
                            {staff.status === "ACTIVE" ? <><XCircle size={14} />{t("suspend")}</> : <><CheckCircle2 size={14} />{t("activate")}</>}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setDeleteDialog(staff)} style={{ color: C.rose }}>
                            <Trash2 size={14} />{t("delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Dialog */}
      <AlertDialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <AlertDialogContent style={{ background: C.card, border: `1px solid rgba(34,211,238,0.18)`, borderRadius: 16 }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: C.t1, fontFamily: "'Outfit',sans-serif" }}>{t("changeStaffStatus")}</AlertDialogTitle>
            <AlertDialogDescription style={{ color: C.t2, fontFamily: "'Outfit',sans-serif" }}>
              {t("areYouSureYouWantTo")} {statusDialog?.status === "ACTIVE" ? t("suspend").toLowerCase() : t("activate").toLowerCase()} {statusDialog?.name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateMutation.isLoading || updateMutation.isPending} style={{ background: C.cardHigh, border: `1px solid ${C.border}`, color: C.t2, borderRadius: 10, fontFamily: "'Outfit',sans-serif" }}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={updateMutation.isLoading || updateMutation.isPending} onClick={handleToggleStatus} style={{ background: C.cyan, color: C.bg, border: "none", borderRadius: 10, fontFamily: "'Outfit',sans-serif", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
              {(updateMutation.isLoading || updateMutation.isPending) && <Loader2 className="animate-spin" size={16} />}
              {t("confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent style={{ background: C.card, border: `1px solid rgba(251,113,133,0.22)`, borderRadius: 16 }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: C.t1, fontFamily: "'Outfit',sans-serif" }}>{t("deleteStaffMember")}</AlertDialogTitle>
            <AlertDialogDescription style={{ color: C.t2, fontFamily: "'Outfit',sans-serif" }}>
              {t("deleteConfirmation")} {deleteDialog?.name}? {t("cannotBeUndone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isLoading || deleteMutation.isPending} style={{ background: C.cardHigh, border: `1px solid ${C.border}`, color: C.t2, borderRadius: 10, fontFamily: "'Outfit',sans-serif" }}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={deleteMutation.isLoading || deleteMutation.isPending} onClick={(e) => { e.preventDefault(); deleteMutation.mutate(deleteDialog.id); }} style={{ background: C.rose, color: "#fff", border: "none", borderRadius: 10, fontFamily: "'Outfit',sans-serif", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
              {(deleteMutation.isLoading || deleteMutation.isPending) && <Loader2 className="animate-spin" size={16} />}
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}